import React, { useContext, useState } from 'react';
import { useClickRef } from '@make-software/csprclick-ui';
import { TransactionStatus } from '@make-software/csprclick-core-types';
import { SvgIcon } from '@make-software/cspr-design';

import styled from 'styled-components';

import LoadingIcon from '@/assets/icons/loading.svg';
import proxyCallerUrl from '@/assets/proxy_caller.wasm?url';
import { Modal, ModalText } from '@/components';
import { BuyStampsForm } from './buy-stamps-form';
import { ConnectedAccountContext } from '@/contexts/ConnectedAccountContext';
import { Args, CLTypeUInt8, CLValue, Hash, PublicKey, SessionBuilder } from 'casper-js-sdk';

const StyledLoadingIcon = styled(SvgIcon)(() => ({
  animationName: 'spin',
  animationDuration: '5000ms',
  animationIterationCount: 'infinite',
  animationTimingFunction: 'linear'
}));

const buildBuyStampsTransaction = async (
  caller: string,
  amount: string,
) => {
  const proxyCallerResponse = await fetch(proxyCallerUrl);
  const contractWasm = new Uint8Array(await proxyCallerResponse.arrayBuffer());

  const amountMotes = `${amount}000000000`;

  const args = Args.fromMap({
    amount: CLValue.newCLUInt512(amountMotes),
    attached_value: CLValue.newCLUInt512(amountMotes),
    entry_point: CLValue.newCLString('buy_stamps'),
    package_hash: CLValue.newCLByteArray(
      Hash.fromHex(config.chainmail_contract_package_hash).toBytes()
    ),
    args: CLValue.newCLList(
      CLTypeUInt8,
      Array.from(Args.fromMap({}).toBytes())
        .map((value) => CLValue.newCLUint8(value))
    )
  });

  const sessionTransaction = new SessionBuilder()
    .from(PublicKey.fromHex(caller))
    .runtimeArgs(args)
    .wasm(contractWasm)
    .payment(config.stamp_transaction_cost * 1000000000)
    .chainName(window.csprclick?.chainName!)
    .build();

  return {
    transaction: {
      Version1: sessionTransaction.toJSON()
    }
  };
};

type ModalScreen = 'form' | 'loading' | 'success' | 'cancelled' | 'error' | null;

interface BuyStampsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BuyStampsModal: React.FC<BuyStampsModalProps> = ({ isOpen, onClose }) => {
  const [modalScreen, setModalScreen] = useState<ModalScreen>(null);
  const connectedAccount = useContext(ConnectedAccountContext);

  const clickRef = useClickRef();

  const handleSignTransaction = async (amount: string) => {
    const caller = connectedAccount?.public_key?.toLowerCase() || '';

    const transaction = await buildBuyStampsTransaction(caller, amount);

    const onStatusUpdate = (status: string, data: any) => {
      if (status === TransactionStatus.CANCELLED) {
        setModalScreen('cancelled');
      }
      else if (status === TransactionStatus.ERROR) {
        console.error('Error: ', data?.error + '(' + data?.errorData + ')');
        setModalScreen('error');
      }
      else if (status === TransactionStatus.PROCESSED) {
        if (data.csprCloudTransaction?.error_message === null) {
          setModalScreen('success');
          setTimeout(() => onClose(), 4000);
        } else {
          console.error('Error: ', data?.error + '(' + data?.errorData + ')');
          setModalScreen('error');
        }
      }
    };

    setModalScreen('loading');

    clickRef?.send(transaction, caller, onStatusUpdate).catch((err: any) => {
      setModalScreen('error');
      alert('Error: ' + err);
    });
  };

  const renderContent = () => {
    switch (modalScreen) {
      case 'loading':
        return <ModalText
          icon={<StyledLoadingIcon src={LoadingIcon} width={100} height={100}/>}
          text="Submitting your request"
        />;
      case 'success':
        return <ModalText text="You have successfully purchased stamps!"/>;
      case 'cancelled':
        return <ModalText text="Your request have been cancelled by the wallet application"/>;
      case 'error':
        return <ModalText text="Something went wrong. Please try again later."/>;
      case 'form':
      default:
        return <BuyStampsForm onConfirm={handleSignTransaction} />;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => {
      onClose();
      setModalScreen('form');
    }}>
      {renderContent()}
    </Modal>
  );
};
